import React, { useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  AdType,
  Attribution,
  MarketingChannel,
  getAdTypeLabel,
  getMarketingChannelLabel,
  messagingChannelLabels
} from '../../../interfaces/attribution.interface';

export interface AttributionPanelProps {
  data: Attribution;
  /** Open on first render. Closed by default: most bookings are direct and the
   *  detail is only interesting when someone is chasing a campaign. */
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Where a booking or reservation came from.
 *
 * Collapsed it answers the question most people actually have — which channel,
 * paid or organic, which campaign — in one line. Expanded it shows the raw
 * parameters, for the times someone is reconciling against the ad platform and
 * needs the exact `utm_content` or click id.
 *
 * ABSENT AND UNKNOWN ARE DIFFERENT, AND SHOWN DIFFERENTLY.
 * `unknown` means we determined we could not tell — an untagged referrer, an
 * offline booking nobody asked about. An empty field means nothing was ever
 * recorded. Collapsing the two would hide how much demand is genuinely
 * unattributable, which is the number worth watching.
 */

const PAID_STYLES: Record<string, string> = {
  [AdType.PAID]: 'bg-amber-50 text-amber-700 border-amber-200',
  [AdType.ORGANIC]: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  [AdType.UNKNOWN]: 'bg-gray-50 text-gray-500 border-gray-200'
};

/** Channels we can name get a solid chip; the two "we don't know" values are
 *  deliberately muted so they do not read as a real channel at a glance. */
const isDeterminedChannel = (channel?: string) =>
  Boolean(channel) && channel !== MarketingChannel.UNKNOWN;

const Field = ({ label, value }: { label: string; value?: string | number }) => {
  const shown = value === undefined || value === null || value === '' ? '—' : String(value);
  const isEmpty = shown === '—';

  return (
    <div className="rounded-lg border border-gray-100 bg-white/80 p-3 min-w-0">
      <span className="block text-xs font-gilroy-medium text-neutral-500 uppercase tracking-wide mb-1">
        {label}
      </span>
      <span
        className={`block text-sm truncate ${
          isEmpty ? 'text-gray-300 font-gilroy-medium' : 'text-gray-800 font-gilroy-bold'
        }`}
        title={shown}
      >
        {shown}
      </span>
    </div>
  );
};

export const AttributionPanel: React.FC<AttributionPanelProps> = ({
  data,
  defaultExpanded = false,
  className = ''
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const hasAnything = Boolean(
    data &&
      (data.marketingChannel ||
        data.utm_source ||
        data.utm_campaign ||
        data.adType ||
        data.gclid ||
        data.fbclid)
  );

  const clickIds = [data.gclid && 'gclid', data.fbclid && 'fbclid', data.ttclid && 'ttclid']
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className={`bg-white rounded-lg shadow-sm bg-gradient-to-b from-indigo-50/70 to-white p-5 ${className}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 rounded"
      >
        <div className="min-w-0 flex items-center gap-3 flex-wrap">
          <p className="text-sm text-indigo-600 font-gilroy-bold leading-tight uppercase tracking-wide">
            Where this came from
          </p>

          {hasAnything ? (
            <>
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-gilroy-bold ${
                  isDeterminedChannel(data.marketingChannel)
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}
              >
                {getMarketingChannelLabel(data.marketingChannel)}
              </span>
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-gilroy-bold ${
                  PAID_STYLES[data.adType || AdType.UNKNOWN]
                }`}
              >
                {getAdTypeLabel(data.adType)}
              </span>
              {data.utm_campaign && (
                <span
                  className="text-xs font-gilroy-medium text-gray-500 truncate max-w-xs"
                  title={data.utm_campaign}
                >
                  {data.utm_campaign}
                </span>
              )}
            </>
          ) : (
            <span className="text-xs font-gilroy-medium text-gray-400">
              Nothing recorded — this booking predates campaign tracking, or came
              through a path that carries none
            </span>
          )}
        </div>

        <ExpandMoreIcon
          className={`shrink-0 text-indigo-400 transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Source" value={data.utm_source} />
            <Field label="Medium" value={data.utm_medium} />
            <Field label="Campaign" value={data.utm_campaign} />
            <Field label="Content" value={data.utm_content} />
            <Field label="Term" value={data.utm_term} />
            <Field
              label="Talked to us via"
              value={
                data.messagingChannel ? messagingChannelLabels[data.messagingChannel] : undefined
              }
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Landing page" value={data.landingPath} />
            <Field label="Referrer" value={data.referrer} />
          </div>

          {/* The click ids themselves are long, opaque and of no use on screen —
              what matters here is whether one exists, because that is what makes
              a booking provably paid and what a conversion sender needs. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Ad click id" value={clickIds || undefined} />
            <Field
              label="Captured"
              value={
                data.attributedAt
                  ? new Date(Number(data.attributedAt)).toLocaleString()
                  : undefined
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributionPanel;
